import { useMemo, useState } from 'react';
import { ArrowRight, Plus, Trash2, X } from 'lucide-react';
import { EXERCISE_LIBRARY, MUSCLE_GROUPS } from './state';
import { canonicalName } from './logic/muscles';
import { FONT_MONO } from './theme';
import { Button, Input } from './ui';

/* Shared mutators for the reusable custom-exercise library. A custom exercise
 * typed in the picker is saved to state.customExercises so it appears in the
 * library on every future visit and its logs can be tracked/benchmarked like
 * any built-in exercise (history & stats key off the exercise name). */
export function makeCustomExerciseHandlers(setState) {
  const onSaveCustom = (name) => setState(prev => {
    const n = (name || '').trim();
    if (!n) return prev;
    const exists = (prev.customExercises || []).some(x => x.toLowerCase() === n.toLowerCase())
      || EXERCISE_LIBRARY.some(ex => ex.name.toLowerCase() === n.toLowerCase());
    if (exists) return prev;
    return { ...prev, customExercises: [...(prev.customExercises || []), n] };
  });
  const onDeleteCustom = (name) => setState(prev => ({
    ...prev,
    customExercises: (prev.customExercises || []).filter(x => x !== name),
  }));
  return { onSaveCustom, onDeleteCustom };
}

/* `aliases` = state.exerciseAliases: a renamed or merged name resolves to
 * its current name whether it is picked or typed, so logs never split again.
 * autoFocus=false keeps the keyboard down and initialMuscle preselects a
 * filter (the swap sheet's "Search all…"). */
export function ExercisePicker({
  t, onPick, onClose, currentName, customExercises = [], onSaveCustom, onDeleteCustom, aliases, autoFocus = true,
  initialMuscle = 'all',
}) {
  const [search, setSearch] = useState('');
  const [muscle, setMuscle] = useState(MUSCLE_GROUPS.includes(initialMuscle) ? initialMuscle : 'all');
  const [custom, setCustom] = useState(currentName || '');

  const library = useMemo(() => {
    const customItems = (customExercises || []).map(name => ({ name, muscle: 'Custom', equipment: 'custom', custom: true }));
    return [...customItems, ...EXERCISE_LIBRARY];
  }, [customExercises]);

  const q = search.trim().toLowerCase();
  const list = useMemo(() => {
    return library.filter(ex => {
      if (muscle !== 'all' && ex.muscle !== muscle) return false;
      if (!q) return true;
      return ex.name.toLowerCase().includes(q) || ex.muscle.toLowerCase().includes(q);
    });
  }, [q, muscle, library]);

  // Old names matching the search, shown as "old → current" shortcuts.
  const renamed = useMemo(() => {
    if (!q || !aliases || typeof aliases !== 'object') return [];
    return Object.keys(aliases)
      .filter(from => from.toLowerCase().includes(q))
      .map(from => ({ from, to: canonicalName(from, aliases) }))
      .filter(r => r.to !== r.from)
      .slice(0, 3);
  }, [q, aliases]);

  /* The name to use: aliases followed, then the library's (or the custom
   * list's) spelling when only the case differs. */
  const resolve = (raw) => {
    const name = canonicalName(raw.trim(), aliases);
    const hit = library.find(ex => ex.name.toLowerCase() === name.toLowerCase());
    return hit ? hit.name : name;
  };
  const choose = (raw) => { onPick(resolve(raw)); onClose(); };

  const applyCustomName = (override) => {
    const raw = (override != null ? override : custom).trim();
    if (!raw) return;
    const name = resolve(raw);
    const exists = library.some(ex => ex.name === name);
    if (!exists && onSaveCustom) onSaveCustom(name);
    onPick(name);
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16,
        // Fixed, not max: a content-sized sheet shrinks to a sliver on a
        // narrow result set and drags the search box down to the bottom of
        // the screen with it. Fixed height pins the field and the first
        // result in place however many matches there are.
        width: '100%', maxWidth: 560, height: '85vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.30)',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            Pick exercise
            {q && (
              <span style={{ fontSize: 11, fontWeight: 600, color: t.textFaint, fontFamily: FONT_MONO }}>
                {list.length} match{list.length === 1 ? '' : 'es'}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 4, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 18px 8px' }}>
          <Input t={t} autoFocus={autoFocus} placeholder="Search exercises…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Muscle filter pills */}
        <div style={{
          display: 'flex', gap: 6, padding: '6px 18px 12px', overflowX: 'auto', whiteSpace: 'nowrap',
          borderBottom: `1px solid ${t.divider}`,
        }}>
          {['all', ...MUSCLE_GROUPS, ...(customExercises.length ? ['Custom'] : [])].map(m => {
            const active = muscle === m;
            return (
              <button key={m} onClick={() => setMuscle(m)} style={{
                padding: '5px 12px', borderRadius: 999, border: `1px solid ${active ? t.accent : t.cardBorder}`,
                background: active ? t.accentSoft : 'transparent', color: active ? t.accent : t.textMuted,
                fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              }}>
                {m === 'all' ? 'All' : m}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {renamed.map(r => (
            <button key={r.from} onClick={() => choose(r.to)} style={{
              width: '100%', textAlign: 'left', padding: '10px 18px', background: t.accentSoft, border: 'none',
              borderBottom: `1px solid ${t.divider}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 13, color: t.textMuted, minHeight: 44,
            }}>
              <span style={{ textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.from}</span>
              <ArrowRight size={13} style={{ flexShrink: 0 }} />
              <span style={{ color: t.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.to}</span>
            </button>
          ))}
          {list.length === 0 && renamed.length > 0 ? null : list.length === 0 ? (
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ color: t.textMuted, fontSize: 13, marginBottom: q ? 14 : 0 }}>
                {q ? <>Nothing matches “{search.trim()}”.</> : 'No exercises in this filter.'}
              </div>
              {/* Typing a name that isn't in the library is the usual reason
                  you're here — don't make it get retyped in the box below. */}
              {q && (
                <Button t={t} variant="primary" onClick={() => { setCustom(search.trim()); applyCustomName(search.trim()); }}
                  style={{ justifyContent: 'center' }}>
                  <Plus size={14} /> Use “{search.trim()}”
                </Button>
              )}
            </div>
          ) : list.map(ex => (
            <div key={ex.name} style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${t.divider}` }}>
              <button onClick={() => choose(ex.name)} style={{
                flex: 1, minWidth: 0, textAlign: 'left', padding: '10px 18px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              }}
              onMouseEnter={e => e.currentTarget.style.background = t.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontSize: 14, color: t.text, display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</span>
                  {ex.custom && (
                    <span style={{
                      flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: t.accent, background: t.accentSoft, border: `1px solid ${t.accentBorder}`, borderRadius: 4, padding: '1px 5px',
                    }}>Custom</span>
                  )}
                </span>
                <span style={{ flexShrink: 0, fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {ex.muscle} · {ex.equipment}
                </span>
              </button>
              {ex.custom && onDeleteCustom && (
                <button onClick={() => onDeleteCustom(ex.name)} title="Remove from library" style={{
                  flexShrink: 0, padding: '0 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                  color: t.textFaint, display: 'flex', alignItems: 'center',
                }}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Custom */}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${t.divider}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
          <Input t={t} placeholder="Custom exercise name…" value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyCustomName(); }} />
          <Button t={t} variant="primary" disabled={!custom.trim()}
            onClick={() => applyCustomName()}>Save &amp; use</Button>
        </div>
      </div>
    </div>
  );
}
